import { useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { useSession } from '@/app/session';
import { Notice } from '@/ui/Panel';
import { BottomTabs } from '@/routes/shell/BottomTabs';
import { MoreSheet } from '@/routes/shell/MoreSheet';
import { Sidebar } from '@/routes/shell/Sidebar';

/**
 * The signed-in frame, in two shapes for two devices.
 *
 * It used to be one shape for both: a twelve-item row that scrolled sideways,
 * with nine more destinations behind a "menu". Measured on a 390px phone that
 * row was 886px wide — five items visible, seven invisible, nothing saying the
 * rest were a swipe away, and the active item never scrolled into view. The
 * original reasoning was that "a drawer costs a tap before every move", which
 * is true and was the wrong trade: a tap is cheaper than a screen the coach
 * does not know exists.
 *
 * So a phone gets a bottom bar in the thumb zone with the four screens of the
 * weekly loop, and everything else one tap away in a list it can read. A
 * desktop gets the sidebar its width has always had room for. Both render
 * `lib/nav.ts`, so there is one list of destinations rather than two
 * that drift.
 */
export function Shell({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const { user, orgId, switchOrg, signOut } = useSession();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const activeOrg = user?.orgs.find((o) => o.id === orgId) ?? user?.orgs[0];

  const handleSignOut = async () => {
    setMoreOpen(false);
    await signOut();
    navigate(localePath(locale, '/login'), { replace: true });
  };

  return (
    <div className="min-h-dvh lg:flex lg:items-start">
      <Sidebar locale={locale} onSignOut={() => void handleSignOut()} />

      <div className="min-w-0 flex-1">
        {/* A phone needs a line telling it whose club this is; on a desktop the
            sidebar already carries the wordmark and the team, so this bar would
            be a second one saying the same thing. */}
        <header
          className="sticky top-0 z-20 border-b border-line bg-surface/95 backdrop-blur lg:hidden"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <div className="flex items-center gap-3 px-4 py-2.5">
            <Link
              to={localePath(locale, '/')}
              className="-my-2 inline-flex min-h-11 shrink-0 items-center font-condensed text-subhead font-bold tracking-tight"
            >
              {t('app.name')}
            </Link>

            {user && user.orgs.length > 1 ? (
              <label className="ms-auto min-w-0">
                <span className="sr-only">{t('nav.team')}</span>
                <select
                  value={orgId ?? ''}
                  onChange={(e) => switchOrg(e.target.value)}
                  className="max-w-[12rem] truncate rounded-(--radius-control) border border-line bg-surface px-2 py-2 text-meta hover:border-line-strong focus:border-ink focus:outline-none"
                >
                  {user.orgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : activeOrg ? (
              <span className="ms-auto min-w-0 truncate text-meta text-ink-faint">
                {activeOrg.name}
              </span>
            ) : null}
          </div>
        </header>

        {user && !user.emailVerified ? (
          <div className="mx-auto max-w-6xl px-4 pt-4">
            <Notice>{t('auth.verifyPending')}</Notice>
          </div>
        ) : null}

        {/* The bottom bar is fixed, so the page has to end above it — and above
            the home indicator under it. Stated as padding-top and padding-bottom
            separately rather than `py-*` plus an override, because two utilities
            setting the same edge is a coin toss. */}
        <main
          className="mx-auto max-w-6xl px-4 pt-6 pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] sm:pt-8 lg:pb-8"
        >
          <Outlet />
        </main>
      </div>

      <BottomTabs
        locale={locale}
        moreOpen={moreOpen}
        onMore={() => setMoreOpen((v) => !v)}
      />
      <MoreSheet
        locale={locale}
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onSignOut={handleSignOut}
      />
    </div>
  );
}
